import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../pdc-platform/src/ts/Glob';
import ParameterFieldBase, { IParameterFieldBaseOptions } from './ParameterFieldBase';
import ParamsValuesEdit, { ILayoutTab } from './ParamsValuesEdit';
import { CKEDITOR_CONFIG_STATIC } from '../../../_common/platform/ts/Utilities';
import ThemesListField from './ThemesListField';
import StringField from './StringField';
import CKEditField from './CKEditField';
import * as appTheme from './appTheme';
import { ActionDescriptions, HandlerDescriptions } from './WidgetBaseModule';
import { IWidgetObjectsCollection } from './Interfaces';
import { Root } from 'react-dom/client';

import { IWidget, IWidgetOptions, IWidgetContextMenuItem } from '../../../pdc-platform/src/ts/Interfaces';
import { IWidgetLayout } from '../../../pdc-platform/src/tsx/layout/Interfaces';

export const CONFIGURATION_CHANGE_DATE_FIELD = 'configurationChangeDate';

interface IWidgetTypes {
    [widgetType: string]: typeof WidgetBase;
}

const DEFAULT_WIDGET_TYPE_GROUP = 'Misc.';

interface IWidgetObjects {
    stores: { destroy() }[];
    options: IWidgetOptions;
}

export interface IMenuItemFlags {
    maximize?: boolean;
    refresh?: boolean;
    resetLayout?: boolean;
    applyFilters?: boolean;
    resetAllFilters?: boolean;
    createWidgetLink?: boolean;
    exportToExcel?: boolean;
    print?: boolean;
}

export class WidgetBase extends ParamsValuesEdit {
    private static _widgetTypes: IWidgetTypes = {};

    public static register(constructor: typeof WidgetBase) {
        WidgetBase._widgetTypes[constructor.moduleName] = constructor;
    }

    public static checkBeforeAdd(widget: IWidget, workspace: any): { result: boolean; errors?: string[] } {
        return {
            result: true
        };
    }

    public static _name: string;
    public static group: string;
    public static moduleName: string;
    public static icon: string = 'asterisk';
    public static namespace: string;

    public static containersNum: number;

    public static themeModule;

    public static actions: ActionDescriptions = [];
    public static handlers: HandlerDescriptions = [];
    //public static handlerFunction: (data, action: Action) => any;

    public static priority: number;

    public static initTheme(glob: IGlob) {
        if (typeof this.themeModule === 'function') {
            this.themeModule(glob);
        }
    }

    public static initThemes(glob: IGlob) {
        _.each(_.keys(WidgetBase._widgetTypes), (key) => {
            var constr = WidgetBase._widgetTypes[key];

            constr.initTheme(glob);
        });
    }

    public static load(): JQueryDeferred<any> {
        return null;
    }

    public getParametersLayout(): any {
        return null;
    }

    public getMenuItems(
        widgetID: number,
        distractFree: boolean,
        theme: appTheme.IValuesCollection
    ): IWidgetContextMenuItem[] {
        return [];
    }

    public static getContainersNum(data: IWidget): number {
        //Was: return (data.values[data.type].widgets && data.values[data.type].widgets.length) || this.containersNum || 1;
        // See Issue 98426 and Issue 98438.
        // Such widgets as pdc-widget-layout have number of widgets > 0 and not specified containersNum in class.ts.
        // But Tabbed Widget Container has containersNum = 2 specified and may have one widget only.
        // So, when containersNum is strictly specified, then it is applied. Otherwise, result is the number of widgets with 1 as deafult.
        if (this.containersNum != null) {
            return this.containersNum;
        }

        const numberOfWidgets = data.values[data.type]?.widgets?.length;

        return numberOfWidgets > 0 ? numberOfWidgets : 1;
    }

    public static getDefaultLayout(...args): IWidgetLayout {
        return null;
    }

    // default: allow edit inner widget layout
    // disabled for Nested layout Widget
    public allowEditLayout(): boolean {
        return true;
    }

    public getThemeObj(values?: { [key: string]: any }): appTheme.IValuesCollection {
        const commonThemes = appTheme.getThemeObj(null);

        if (values == null) {
            values = this.values;
        }

        if (values != null) {
            return appTheme.extend({}, commonThemes, values['___theme'], values.theme?._custom);
        } else {
            return appTheme.extend({}, commonThemes);
        }
    }

    public getTabID(): number {
        return this.values != null ? this.values['___tabID'] : null;
    }

    public static getMenuItemFlags(): IMenuItemFlags {
        return {
            maximize: true,
            refresh: true,
            resetLayout: false,
            applyFilters: false,
            resetAllFilters: false,
            createWidgetLink: true,
            exportToExcel: false,
            print: true
        };
    }

    public static putInObjectsCollection(
        options: IWidgetOptions,
        roots: Root[],
        data: IWidget,
        stores: { destroy() }[],
        objectsCollection: IWidgetObjectsCollection
    ) {
        const storeKey = WidgetBase.getStoreKey(data.id, options.tab);

        return (objectsCollection[storeKey] = {
            stores: stores,
            options: options,
            staticWidgetID: data.id,
            roots: roots
        });
    }

    public static findInObjectsCollection(objectsCollection: IWidgetObjectsCollection, staticWidgetID: number) {
        return _.find(objectsCollection, (obj) => {
            return obj.staticWidgetID === staticWidgetID;
        });
    }

    private static getStoreKey(id: number, tab: number) {
        return tab != null ? id + '-' + tab : id;
    }

    public static destroyWidget(
        options: IWidgetOptions,
        data: IWidget,
        objectsCollection: IWidgetObjectsCollection,
        beforeDestroy?: (obj?: IWidgetObjects) => void
    ) {
        // See Issue 94912. Maximized Widgets should not clean up widgets frm the original Workspace.
        if (options.maximized) {
            return;
        }

        const storeKey = WidgetBase.getStoreKey(data.id, options.tab);
        const objets = objectsCollection[storeKey];

        if (objets == null) {
            return;
        }

        if (typeof beforeDestroy === 'function') {
            beforeDestroy(objets);
        }

        if (objets.stores != null) {
            objets.stores.forEach((store) => {
                if (store != null && store.destroy != null) {
                    store.destroy();
                }
            });
        }

        delete objectsCollection[storeKey];

        if (objets.roots != null) {
            objets.roots.forEach((root) => root.unmount());
        }
    }

    public getWidgetLinkData(id, glob?): any {
        return null;
    }

    public getDefaultTabs(tabs: ILayoutTab[]) {
        const result = [];
        const themeTab = _.findWhere(tabs, { name: 'Options' });
        const helpTab = _.findWhere(tabs, { name: 'Help' });

        if (themeTab == null) {
            result.push({
                name: 'Options',
                groups: [
                    {
                        rows: ['theme']
                    }
                ]
            });
        }

        if (helpTab == null) {
            result.push({
                name: 'Help',
                groups: [
                    {
                        rows: ['tooltip', 'description']
                    }
                ]
            });
        }

        return result;
    }

    public getParameters(glob: IGlob): {
        [fieldName: string]: ParameterFieldBase<IParameterFieldBaseOptions>;
    } {
        return {};
    }

    public getDefaultParameters(glob: IGlob): {
        [fieldName: string]: ParameterFieldBase<IParameterFieldBaseOptions>;
    } {
        // this.values.___theme is set in WidgetData#getWidgetInstance
        const _constructor: any = this.constructor;

        let widgetValues = this.values; //LL: 15.03.20 to access Widget Props in Theme
        //LL:   (NO, do in ThemesListField)  Here and there
        if (widgetValues != null) {
            widgetValues = $.extend(true, {}, widgetValues);
        }

        const getEditorConfig = () => {
            return _.extend({}, CKEDITOR_CONFIG_STATIC, {
                height: null,
                width: null,
                extraPlugins: 'image2,pdc-resource-manager'
            });
        };

        return {
            theme: new ThemesListField(
                this.glob,
                'Inherit Options from Themes',
                [],
                {},
                {
                    savedState: this.savedState_ThemesListField, //LL: 27.08.20: TRY: //LL  for ThemesListField to save internal state through component rebuild
                    widgetValues: widgetValues, //LL: 15.03.20 to access Widget Props in Theme
                    widgetTypeName: _constructor.moduleName, //LL: 08.03.20 ~~"pdc-widget-chart"
                    parentTheme: this.values['___theme'],
                    levels: ['widget', _constructor.moduleName],
                    themeNodeIdToExpand: this.values['themeNodeIdToExpand'] //VR: WI80078
                }
            ),

            role: new StringField(this.glob, 'Provider', [], 'default'),

            tooltip: new StringField(this.glob, 'Header Tooltip', [], null, { allowNull: true }),

            description: new CKEditField(this.glob, 'Description', [], null, {
                fillContainer: true,
                allowNull: true,
                editorConfig: getEditorConfig()
            }),

            configurationChangeDate: new StringField(this.glob, 'Configuration Change Date', [], null, {
                allowNull: true
            })
        };
    }

    public static getTypes() {
        var arr = _.map(
            _.keys(WidgetBase._widgetTypes),

            function (key) {
                var constr = WidgetBase._widgetTypes[key];

                return {
                    name: constr._name,
                    moduleName: constr.moduleName,
                    icon: constr.icon,
                    group: constr.group || DEFAULT_WIDGET_TYPE_GROUP
                };
            }
        );

        return arr;
    }

    public static getConstructorByName(moduleName: string) {
        return this._widgetTypes[moduleName];
    }

    public setLock(fieldName, value) {
        this.locked[fieldName] = value;
    }

    private filterDefaults(_values, parameters) {
        return _.pick(_values, (value, key) => {
            const param = parameters[key];

            if (param == null) {
                return false;
            }

            return !_.isEqual(param.getDefault(), value);
        });
    }

    //LL: 27.08.20: TRY
    private savedState_ThemesListField: any; //LL  for ThemesListField to save internal state through component rebuild

    constructor(
        values?: { [fieldName: string]: any },
        public locked?: { [fieldName: string]: any },

        ignoreValidation: boolean = false,
        public glob: IGlob = {}
    ) {
        super();
        //LL: 27.08.20: TRY
        this.savedState_ThemesListField = {};

        this.values = values;
        this.valuesToSave = _.clone(values);
        this.parameters = _.extend(this.getParameters(glob), this.getDefaultParameters(glob));
        this.paramsLayout = this.getParametersLayout();

        if (this.paramsLayout != null) {
            this.paramsLayout.tabs = this.paramsLayout.tabs.concat(this.getDefaultTabs(this.paramsLayout.tabs));
        }

        //LL: 15.03.20: moved up  NO, bad
        this.values = _.defaults(this.values || {}, this.getDefault());

        this.valuesToSave = this.filterDefaults(this.valuesToSave, this.parameters);

        this.validate();

        if (this.isInvalid && ignoreValidation === false) {
            const widgetName = this.values.name ?? '';

            console.log(`Widget with name '${widgetName}' has invalid configuration`);
            this.fixValues();

            const { isValid, error } = this.validate();

            if (!isValid && error != null) {
                console.log(error);
            }
        }
    }
}
